import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelection } from '@cube3/state/src/redux/components/useSelection';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';

import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import {
  ExportMultiAssetsInfo,
  ExportSingleAssetInfo
} from '../prefabs/ExportAssetsInfo';
import { Divider } from '@cube3/ui/src/Surface/Divider';
import { useExportOptions } from '../hooks/useExportOptions';
import { Padding } from '../../Contracts/prefabs/Padding';
import { useExportTargetDefaults } from '../../AdminCenter/views/WorkspaceIntegrationsView/hooks/useExportTargetDefaults';
import { useDispatch } from 'react-redux';
import { queueTabs, toggleMediaqueue } from '@cube3/state/src/redux/ducks/ui';
import { TikTokParameters } from '@cube3/common/model/resource-types';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { TikTokCreatorInfo } from '@cube3/common/model/resource-types';

import { DisabledTitle } from '../prefabs/DisabledTitle';
import { assetMimeTypes } from '@cube3/state/types';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { objectFromValues } from '@cube3/forms/src/utils/objectToFormValues';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import { validEmailaddress } from '@cube3/forms/src/utils/validators';

interface Props {
  modalContext: {
    creator_info: TikTokCreatorInfo;
    /** selected connected account from previous step */
    exportTargetId: string;
    /** target type used for retrieving default values  */
    exportTargetTypeId: string;
  };
}

/**
 * Dynamically render options form
 */

const privacyLevelToDisplayName = (privacyLevel: string) => {
  switch (privacyLevel) {
    case 'SELF_ONLY':
      return 'Yourself only';
    case 'FOLLOWER_OF_CREATOR':
      return 'Your followers';
    case 'MUTUAL_FOLLOW_FRIENDS':
      return 'Friends of friends';
    case 'PUBLIC':
      return 'Public';
    default:
      return 'Unknown';
  }
};

export const TikTokExportOptionsModal: React.FC<Props> = (props) => {
  const { creator_info, exportTargetId, exportTargetTypeId } =
    props.modalContext;

  const { selection: initialSelection } = useSelection();

  const { current: selection } = useRef(initialSelection);

  const resources = []
    .concat(selection.length > 0 ? selection : undefined)
    .filter((r) => !!r);

  const selectedVideosOnly =
    resources.length &&
    resources.every((r) => [assetMimeTypes.VIDEO].includes(r.mime_type));

  const fields = useMemo(() => {
    return [
      selection?.length === 1
        ? {
            id: 'caption',
            label: 'Caption',
            type: 'string' as const,
            required: selection.length === 1,
            disabled: selection.length > 1
          }
        : {
            id: 'caption',
            label: 'Caption',
            type: 'custom' as const,
            disabled: false,
            component: DisabledTitle
          },
      {
        id: 'post_directly',
        label: 'Publish immediately',
        type: 'toggle' as const
      },
      {
        id: 'privacy_level',
        label: 'Who can view this',
        type: 'select' as const,
        options: creator_info.privacy_level_options.map((e) => ({
          id: e,
          display_name: privacyLevelToDisplayName(e),
          value: e
        }))
      },
      {
        id: 'sharing',
        label: 'Allow users to:',
        type: 'checkbox' as const,
        options: [
          {
            id: 'comment_disabled',
            display_name: 'Comment',
            value: 'comment_disabled'
          },
          {
            id: 'duet_disabled',
            display_name: 'Duet',
            value: 'duet_disabled',
            disabled: !selectedVideosOnly
          },
          {
            id: 'stitch_disabled',
            display_name: 'Stitch',
            value: 'stitch_disabled',
            disabled: !selectedVideosOnly
          }
        ]
      },
      {
        id: 'branded_content',
        label: 'Branded content',
        type: 'select' as const,
        options: [
          { id: 'no', display_name: 'No', value: 'no' },
          {
            id: 'brand_content_toggle',
            display_name: 'Promoting a third-party business',
            value: 'brand_content_toggle'
          },
          {
            id: 'brand_organic_toggle',
            display_name: "Promoting the creator's own business",
            value: 'brand_organic_toggle'
          }
        ]
      },
      { id: 'divider', type: 'divider' },
      {
        id: 'recipients',
        label: 'Send export notifications to',
        type: 'multiselect',
        validate: validEmailaddress,
        allowAddingOfNewSuggestions: true
      }
    ] as Field[];
  }, [selection, creator_info]);

  const [defaults, defaultsStatus] =
    useExportTargetDefaults(exportTargetTypeId);

  const initialValues = useMemo(() => {
    return [
      {
        id: 'display_name',
        value: selection.length === 1 ? selection[0]['display_name'] : undefined
      },
      { id: 'branded_content', value: 'no' },
      { id: 'privacy_level', value: 'SELF_ONLY' },
      // {
      //   id: 'comment_disabled',
      //   value:
      //     selection.length === 1
      //       ? selection[0]['comment_disabled']
      //         ? 'yes'
      //         : 'no'
      //       : 'yes'
      // },
      ...Object.keys(defaults || {}).map((k) => {
        return { id: k, value: defaults[k] };
      })
    ];
  }, [selection, defaults]);

  const { fields: fieldsWithState, ...formState } = useGenericFormState({
    fields,
    initialValues
  });

  const { previousModal, closeAllModals, goBackModals } = useModalActions();
  const { createExportStatus, handleCreateExport, exportBatchId } =
    useExportOptions<TikTokParameters>({
      selection,
      exportTargetId
    });

  const handleSubmit = useCallback(() => {
    const { values } = formState;
    if (!values) return;
    const formValue = objectFromValues(values);

    //map parameters the way we like
    const sharing = formValue.sharing as string[];
    const params = {
      post_directly: formValue.post_directly as boolean,
      comment_disabled: !sharing?.includes('comment_disabled'),
      duet_disabled: !sharing?.includes('duet_disabled'),
      stitch_disabled: !sharing?.includes('stitch_disabled'),
      privacy_level: formValue.privacy_level as string,
      caption: formValue.caption as string,
      branded_content: formValue.branded_content as string
    };
    handleCreateExport({
      recipients: formValue.recipients as string[],
      // map pa
      integration_parameters: {
        ...params
      }
    });
  }, [handleCreateExport, formState]);

  const dispatch = useDispatch();

  // open export status view modal
  useEffect(() => {
    if (exportBatchId && createExportStatus === statuses.SUCCESS) {
      closeAllModals();
      dispatch(toggleMediaqueue(true, queueTabs.EXPORTS));
    }
  }, [createExportStatus, exportBatchId, formState, closeAllModals, dispatch]);

  const handleCancel = useCallback(() => {
    goBackModals(2);
  }, [goBackModals]);

  return (
    <ModalMenuUI
      title="Export to TikTok • Options"
      wide={true}
      multiplyContentSpace={10}
      loading={
        defaultsStatus !== statuses.SUCCESS ||
        (createExportStatus &&
          createExportStatus !== statuses.SUCCESS &&
          createExportStatus !== statuses.FAILED)
      }
      onCloseEvent={handleCancel}
      footerLeftComponent={
        <Button
          text={'Previous'}
          onClick={previousModal}
          colorVariant={'ghost2'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={handleCancel}
            disabled={false}
          />
          <Button
            disabled={!formState?.valid}
            colorVariant={'filled1'}
            text={'Export'}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <Padding>
        {selection.length > 0 ? (
          <>
            {selection.length > 1 ? (
              <ExportMultiAssetsInfo selection={selection} />
            ) : (
              <ExportSingleAssetInfo selection={selection?.[0]} />
            )}
            <Divider gutter={true} />

            <Padding>
              {hasResolved(defaultsStatus) && initialValues && (
                <GenericForm
                  fields={fieldsWithState}
                  form={'radio_export_form'}
                />
              )}
            </Padding>
          </>
        ) : null}
      </Padding>
    </ModalMenuUI>
  );
};
